import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const LINKS = [
  { id: "about", label: "About" },
  { id: "process", label: "Process" },
  { id: "handoff", label: "Work" },
  { id: "pricing", label: "Pricing" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -80, duration: 1.2 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
}

export default function Nav({ onOpenRecon }) {
  const [hovered, setHovered] = useState(null);
  const [active, setActive] = useState("about");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    LINKS.forEach((l) => {
      const el = document.getElementById(l.id);
      if (el) observer.observe(el);
    });
    return () => {
      window.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, []);

  const highlight = hovered || active;

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed inset-x-0 top-0 z-50"
    >
      <div
        className={`mx-auto flex max-w-7xl items-center justify-between px-5 py-4 transition-colors duration-500 md:px-8 ${
          scrolled
            ? "bg-paper/80 backdrop-blur-md border-b border-hairline"
            : "border-b border-transparent"
        }`}
      >
        {/* Wordmark */}
        <button
          data-testid="nav-wordmark"
          onClick={() => scrollToId("top")}
          className="flex items-center gap-2.5 font-serif text-2xl tracking-tight text-ink transition-colors duration-300"
        >
          <img
            src="/logo-icon.png"
            alt="Zigler Designs owl mark"
            className="h-12 w-12 object-contain"
          />
          <span>
            <span className="text-pine">Zigler</span> Designs
          </span>
        </button>

        {/* Morphing pill nav (desktop) */}
        <nav
          className="relative hidden items-center rounded-full border border-hairline bg-ink/5 px-1.5 py-1.5 md:flex"
          onMouseLeave={() => setHovered(null)}
        >
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onMouseEnter={() => setHovered(l.id)}
              onClick={() => scrollToId(l.id)}
              className="relative px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em]"
            >
              {highlight === l.id && (
                <motion.span
                  layoutId="nav-pill"
                  className="absolute inset-0 rounded-full bg-pine"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                />
              )}
              <span
                className={`relative z-10 transition-colors duration-200 ${
                  highlight === l.id ? "text-paper" : "text-ink-dim"
                }`}
              >
                {l.label}
              </span>
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-start-build"
            onClick={onOpenRecon}
            className="hidden rounded-full bg-pine px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-paper transition-transform duration-300 hover:scale-[1.03] active:scale-95 md:inline-block"
          >
            Start a build
          </button>

          {/* Mobile menu toggle */}
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-hairline md:hidden"
            aria-label="Toggle menu"
          >
            <span className="flex flex-col gap-1">
              <span className="h-px w-4 bg-ink" />
              <span className="h-px w-4 bg-ink" />
              <span className="h-px w-4 bg-ink" />
            </span>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-hairline bg-paper/95 backdrop-blur-md md:hidden"
          >
            <div className="flex flex-col px-5 py-2">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => {
                    scrollToId(l.id);
                    setMenuOpen(false);
                  }}
                  className="border-b border-hairline py-3 text-left font-mono text-xs uppercase tracking-[0.18em] text-ink-dim last:border-0"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-start-build"
                onClick={() => {
                  setMenuOpen(false);
                  onOpenRecon();
                }}
                className="my-3 rounded-full bg-pine px-5 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-paper"
              >
                Start a build
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
